import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Badge,
  Input,
  Skeleton,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@asm/ui";
import {
  Globe,
  Server,
  Network,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Clock,
  ArrowLeft,
  ExternalLink,
  TrendingUp,
  AlertTriangle,
  Cloud,
} from "lucide-react";

// Cloud provider icons mapping
const CLOUD_PROVIDER_ICONS: Record<string, { icon: string; name: string }> = {
  aws: { icon: "/icons/aws.svg", name: "Amazon Web Services" },
  amazon: { icon: "/icons/aws.svg", name: "Amazon Web Services" },
  azure: { icon: "/icons/azure.svg", name: "Microsoft Azure" },
  microsoft: { icon: "/icons/azure.svg", name: "Microsoft Azure" },
  gcp: { icon: "/icons/gcp.svg", name: "Google Cloud Platform" },
  google: { icon: "/icons/gcp.svg", name: "Google Cloud Platform" },
  cloudflare: { icon: "/icons/cloudflare.svg", name: "Cloudflare" },
  digitalocean: { icon: "/icons/digitalocean.svg", name: "DigitalOcean" },
  fastly: { icon: "/icons/fastly.svg", name: "Fastly" },
  linode: { icon: "/icons/linode.svg", name: "Linode" },
  akamai: { icon: "/icons/akamai.svg", name: "Akamai" },
  ovh: { icon: "/icons/ovh.svg", name: "OVH" },
  hetzner: { icon: "/icons/hetzner.svg", name: "Hetzner" },
  vultr: { icon: "/icons/vultr.svg", name: "Vultr" },
  oracle: { icon: "/icons/oracle.svg", name: "Oracle Cloud" },
  ibm: { icon: "/icons/ibm.svg", name: "IBM Cloud" },
  alibaba: { icon: "/icons/alibaba.svg", name: "Alibaba Cloud" },
};

// AWS service-specific icons
const AWS_SERVICE_ICONS: Record<string, { icon: string; name: string }> = {
  cloudfront: { icon: "/icons/aws-cloudfront.svg", name: "AWS CloudFront" },
  ec2: { icon: "/icons/aws-ec2.svg", name: "AWS EC2" },
  s3: { icon: "/icons/aws-s3.svg", name: "AWS S3" },
  lambda: { icon: "/icons/aws-lambda.svg", name: "AWS Lambda" },
  elb: { icon: "/icons/aws-elb.svg", name: "AWS ELB" },
  elasticloadbalancing: { icon: "/icons/aws-elb.svg", name: "AWS ELB" },
  globalaccelerator: { icon: "/icons/aws.svg", name: "AWS Global Accelerator" },
  apigateway: { icon: "/icons/aws.svg", name: "AWS API Gateway" },
  route53: { icon: "/icons/aws.svg", name: "AWS Route 53" },
  amazon: { icon: "/icons/aws.svg", name: "AWS" },  // Generic fallback
};

function getCloudInfo(
  provider: string | null | undefined,
  service: string | null | undefined
): { icon: string; name: string } | null {
  if (!provider) return null;

  const providerKey = provider.toLowerCase().replace(/[^a-z]/g, "");

  // For AWS, check service-specific icons first
  if (providerKey === "aws" || providerKey === "amazon") {
    if (service) {
      const serviceKey = service.toLowerCase().replace(/[^a-z]/g, "");
      const serviceInfo = AWS_SERVICE_ICONS[serviceKey];
      if (serviceInfo) return serviceInfo;
    }
    // Fallback to generic AWS
    return CLOUD_PROVIDER_ICONS.aws;
  }

  // For other providers
  for (const [k, v] of Object.entries(CLOUD_PROVIDER_ICONS)) {
    if (providerKey.includes(k)) return v;
  }
  return null;
}

import { assetsApi, domainsApi, type Asset } from "@/lib/api";

// Simple relative time formatter
function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? "s" : ""}`;
  if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? "s" : ""}`;
  if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? "s" : ""}`;
  return "just now";
}

type AssetTypeFilter = "all" | "subdomain" | "ip" | "port";
type StatusFilter = "all" | "active" | "stale";

export default function DomainAssets() {
  const { id: domainId } = useParams<{ id: string }>();
  const [search, setSearch] = useState("");
  const [assetType, setAssetType] = useState<AssetTypeFilter>("all");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [page, setPage] = useState(1);
  const perPage = 25;

  // Fetch domain info to get the domain name
  const { data: domains } = useQuery({
    queryKey: ["domains"],
    queryFn: () => domainsApi.list(),
  });

  const domainInfo = domains?.find((d) => d.id === domainId);

  // Fetch domain-specific stats
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["domain-asset-stats", domainId],
    queryFn: () => assetsApi.getStatsByDomain(domainId!),
    enabled: !!domainId,
  });

  // Fetch assets for this domain
  const { data: assetsData, isLoading: assetsLoading, refetch } = useQuery({
    queryKey: ["domain-assets", domainId, assetType, status, search, page],
    queryFn: () =>
      assetsApi.listByDomain(domainId!, {
        asset_type: assetType === "all" ? undefined : assetType,
        status: status === "all" ? undefined : status,
        search: search || undefined,
        page,
        per_page: perPage,
      }),
    enabled: !!domainId,
  });

  const totalPages = assetsData ? Math.ceil(assetsData.total / perPage) : 0;

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      <nav className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/assets" className="hover:text-foreground transition-colors">
          Assets
        </Link>
        <span>/</span>
        <span className="text-foreground font-medium">{domainInfo?.domain || "Domain"}</span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/assets">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <Globe className="h-8 w-8 text-primary" />
              {domainInfo?.domain || "Loading..."}
            </h1>
            <p className="text-muted-foreground">
              Assets discovered for this domain
            </p>
          </div>
        </div>
        <Button onClick={() => refetch()} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Assets</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats?.total_count || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {stats?.active_count || 0} active
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Subdomains</CardTitle>
            <Globe className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold">{stats?.subdomains_count || 0}</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">IP Addresses</CardTitle>
            <Server className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold">{stats?.ips_count || 0}</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">New This Week</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats?.new_this_week || 0}</div>
                {(stats?.stale_count || 0) > 0 && (
                  <p className="text-xs text-yellow-600 dark:text-yellow-500">
                    <AlertTriangle className="h-3 w-3 inline mr-1" />
                    {stats?.stale_count} stale
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search assets..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-10"
              />
            </div>

            <Select
              value={assetType}
              onValueChange={(v: AssetTypeFilter) => {
                setAssetType(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Asset Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="subdomain">Subdomains</SelectItem>
                <SelectItem value="ip">IP Addresses</SelectItem>
                <SelectItem value="port">Ports</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={status}
              onValueChange={(v: StatusFilter) => {
                setStatus(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="stale">Stale</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Assets List */}
      <Card>
        <CardHeader>
          <CardTitle>Assets</CardTitle>
          <CardDescription>
            {assetsData?.total || 0} assets found for {domainInfo?.domain || "this domain"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {assetsLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : assetsData?.items.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Globe className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No assets found</p>
              <p className="text-sm">
                Run a scan on this domain to discover assets
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Header Row */}
              <div className="grid grid-cols-[auto_auto_minmax(120px,1fr)_160px_minmax(180px,1fr)_110px_70px_auto] items-center gap-3 px-4 py-2 text-xs font-medium text-muted-foreground border-b">
                <div className="w-6 text-center">Cloud</div>
                <div className="w-10 text-center">Type</div>
                <div>Identifier</div>
                <div>Type / Service</div>
                <div>Hostnames</div>
                <div>Last Seen</div>
                <div className="text-center">Status</div>
                <div className="w-4"></div>
              </div>
              {assetsData?.items.map((asset) => (
                <AssetRow key={asset.id} asset={asset} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AssetRow({ asset }: { asset: Asset }) {
  const getAssetIcon = (type: string) => {
    switch (type) {
      case "subdomain":
        return <Globe className="h-4 w-4 text-blue-500" />;
      case "ip":
        return <Server className="h-4 w-4 text-green-500" />;
      case "port":
        return <Network className="h-4 w-4 text-purple-500" />;
      default:
        return <Globe className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge variant="default" className="bg-green-500/10 text-green-600 hover:bg-green-500/20">Active</Badge>;
      case "stale":
        return <Badge variant="secondary" className="bg-yellow-500/10 text-yellow-600">Stale</Badge>;
      case "removed":
        return <Badge variant="destructive">Removed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Get cloud info for IP and port assets
  const cloudProvider = asset.metadata?.cloud_provider as string | undefined;
  const cloudService = asset.metadata?.cloud_service as string | undefined;
  const isCloud = asset.metadata?.is_cloud as boolean | undefined;
  const cloudInfo = (asset.asset_type === "ip" || asset.asset_type === "port")
    ? getCloudInfo(cloudProvider, cloudService)
    : null;

  // Get FQDNs for IP and port assets
  const fqdns = (asset.metadata?.fqdns as string[] | undefined) || [];

  return (
    <Link to={`/assets/${asset.id}`}>
      <div className="grid grid-cols-[auto_auto_minmax(120px,1fr)_160px_minmax(180px,1fr)_110px_70px_auto] items-center gap-3 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer group">
        {/* Column 1: Cloud Icon */}
        <div className="w-6 flex items-center justify-center">
          {(asset.asset_type === "ip" || asset.asset_type === "port") && (
            cloudInfo ? (
              <img
                src={cloudInfo.icon}
                alt={cloudInfo.name}
                className="h-5 w-5"
                title={cloudInfo.name}
              />
            ) : isCloud ? (
              <Cloud className="h-5 w-5 text-muted-foreground" />
            ) : null
          )}
        </div>

        {/* Column 2: Asset Type Icon */}
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted shrink-0">
          {getAssetIcon(asset.asset_type)}
        </div>

        {/* Column 3: Identifier */}
        <div className="min-w-0">
          <p className="font-medium font-mono text-sm truncate" title={asset.identifier}>
            {asset.identifier}
          </p>
        </div>

        {/* Column 4: Type Info (Type · Cloud Service) */}
        <div className="text-xs">
          <span className="text-muted-foreground">
            {asset.asset_type === "ip" ? "IP" : asset.asset_type.charAt(0).toUpperCase() + asset.asset_type.slice(1)}
          </span>
          {cloudInfo && (
            <>
              <span className="text-muted-foreground"> · </span>
              <span className="text-blue-500">{cloudInfo.name}</span>
            </>
          )}
        </div>

        {/* Column 5: Hostnames (FQDNs) */}
        <div className="min-w-0">
          {(asset.asset_type === "ip" || asset.asset_type === "port") && fqdns.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {fqdns.slice(0, 3).map((fqdn, i) => (
                <span
                  key={i}
                  className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded"
                >
                  {fqdn}
                </span>
              ))}
              {fqdns.length > 3 && (
                <span className="text-xs text-muted-foreground">+{fqdns.length - 3}</span>
              )}
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </div>

        {/* Column 6: Last Seen */}
        <div className="text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3 shrink-0" />
            <span>{formatRelativeTime(new Date(asset.last_seen_at))} ago</span>
          </div>
        </div>

        {/* Column 7: Status */}
        <div className="flex justify-center">
          {getStatusBadge(asset.status)}
        </div>

        {/* Column 8: Arrow */}
        <div>
          <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
    </Link>
  );
}
